import { reactive, computed, markRaw } from 'vue';
/////////////////////////////////////////
// READ THIS BEFORE IMPORTING ANYTHING //
/////////////////////////////////////////
// EventBus is a reactive object that is created (and initialized) when this file is imported.
//
// Circular dependencies can create a complex situation where a dependency imported by EventBus.js is not loaded before the EventBus object is created.

// If you need to import something here, make sure that EventBus.js is imported very early in the application initialization (one of the first imports in main.js).
/////////////////////////////////////////

import { GetAllProductImages } from 'appRoot/api/CMSUserData';
import { CmsEventStream, CmsKey } from 'appRoot/scripts/CMSEventStream';

/**
 * This object can be used to provide "global" reactive state shared between components.
 * Simply read and write properties of the EventBus object; reads made from within computed properties, watchers, and templates are reactive.
 * The EventBus is good for things that need to be shared between components with fast reactivity and should not be persisted between page reloads.
 * EventBus is orders of magnitude faster than the vuex store.
 */
const EventBus = reactive({
	windowWidth: -1,
	windowHeight: -1,
	pageXOffset: 0,
	pageYOffset: 0,
	mouseX: 0,
	mouseY: 0,
	mouseClientX: 0,
	mouseClientY: 0,
	topNavHeight: 0,
	controlBarHeight: 0,
	footerHeight: 0,
	editorMenuBarHeight: 0,
	tooltipHtml: "", // HTML shown in a tooltip that follows the cursor.
	mouseUps: 0, // Counter of mouse up events at the document level.
	pushNotificationsAvailable: false, // True if all preconditions for push notification registration have been met.
	notificationPermission: "default", // The latest value of Notification.permission. May be "default", "granted", "denied"
	pushSubscription: null, // Push notification subscription object. null if the browser is not registered for push.
	mobileLayoutMediaQuery: null,
	mobileLayout: false,
	aceEditorCounter: 0,
	productImageData: { rootIdMap: {}, imageMap: {} },
	browserInstanceId: sessionStorage.myGuid,
	cmsEventStream: markRaw(new CmsEventStream()), // markRaw: all of CmsEventStream's state lives in closures; there is nothing for Vue to make reactive.
	cmsConsoleConnected: false,
	tamStatus: null, // TitleArchiveManager status
	contentSyncStatus: null, // Status of ContentSync (shows what each Agent is doing related to CMS archive or metadata deployment or installation).
	loeStatus: null, // Status of LoE creation
	productRootMap: {}, // Map of Product Root instances keyed by ProductRootId, assigned a new map each time the CMS homepage is loaded.
	myStarredProductRootIds: {},
	myStarredProductRevisions: {},
	isOnCms: false,
	movingItem: null,
	undoStack: null,
	redoStack: null,
	// computed properties (computed refs are auto-unwrapped when read through the reactive EventBus object)
	routeQueryExpand: computed(() =>
	{
		// window.router is used rather than myApp.$route because this can be evaluated during the app's first render, before window.myApp is assigned.
		// (Reading currentRoute.value here still registers a reactive dependency on the route.)
		let expand = window.router.currentRoute.value.query.expand;
		if (!expand)
			expand = "";
		return expand;
	}),
	expandedProductRootIdsArray: computed(() =>
	{
		let arr = [];
		let idStrings = EventBus.routeQueryExpand.split(',');
		for (let i = 0; i < idStrings.length; i++)
		{
			let id = parseInt(idStrings[i]);
			if (!isNaN(id))
				arr.push(id);
		}
		return arr;
	}),
	expandedProductRootIdsMap: computed(() =>
	{
		let map = {};
		let arr = EventBus.expandedProductRootIdsArray;
		for (let i = 0; i < arr.length; i++)
			map[arr[i]] = true;
		return map;
	}),
	verticalConsumers: computed(() =>
	{
		return EventBus.topNavHeight + EventBus.footerHeight + EventBus.editorMenuBarHeight;
	}),
	// methods (these intentionally reference EventBus rather than `this` so they can be used directly as event listeners)
	OnResize(event)
	{
		EventBus.windowWidth = window.innerWidth;
		EventBus.windowHeight = window.innerHeight;
		EventBus.topNavHeight = MeasureHeightOfId("topNav");
		EventBus.controlBarHeight = MeasureHeightOfId("controlBar");
		EventBus.footerHeight = MeasureHeightOfId("appFooter");
		EventBus.editorMenuBarHeight = MeasureHeightOfId("editorMenuBar");
	},
	OnScroll(event)
	{
		EventBus.pageXOffset = window.pageXOffset;
		EventBus.pageYOffset = window.pageYOffset;
	},
	OnMouseMove(event)
	{
		EventBus.mouseX = event.pageX;
		EventBus.mouseY = event.pageY;
		EventBus.mouseClientX = event.clientX;
		EventBus.mouseClientY = event.clientY;
	},
	OnMouseUp(event)
	{
		EventBus.mouseUps++;
	},
	stopMovingItem()
	{
		if (EventBus.movingItem)
		{
			EventBus.movingItem = null;
			EventBus.tooltipHtml = "";
		}
	},
	onMobileLayoutChanged(mediaQuery)
	{
		EventBus.mobileLayout = !mediaQuery.matches;
	},
	LoadProductImages()
	{
		GetAllProductImages()
			.then(result =>
			{
				if (result.success)
				{
					EventBus.productImageData = { rootIdMap: result.productRootIdToImageIdMap, imageMap: result.productImages };
				}
				else
					toaster.error(result.error);
			})
			.catch(err =>
			{
				toaster.error(err);
			});
	}
});
// Initialization logic, previously in the created() hook of the Vue-2-instance-based EventBus.
// The EventBus lives for the lifetime of the page, so nothing here is ever torn down.
(function InitializeEventBus()
{
	if (!sessionStorage.myGuid)
		sessionStorage.myGuid = appContext.guid;
	EventBus.browserInstanceId = sessionStorage.myGuid;

	let ss = sessionStorage.getItem("et_eb_undoStack");
	if (ss)
		EventBus.undoStack = JSON.parse(ss);
	ss = sessionStorage.getItem("et_eb_redoStack");
	if (ss)
		EventBus.redoStack = JSON.parse(ss);

	window.addEventListener('resize', EventBus.OnResize);
	window.addEventListener('scroll', EventBus.OnScroll);
	document.addEventListener('mousemove', EventBus.OnMouseMove);
	document.addEventListener('mouseup', EventBus.OnMouseUp);
	EventBus.OnResize();

	if (window.matchMedia)
	{
		EventBus.mobileLayoutMediaQuery = window.matchMedia("(min-width: 600px)");
		EventBus.mobileLayoutMediaQuery.addListener(EventBus.onMobileLayoutChanged);
		EventBus.onMobileLayoutChanged(EventBus.mobileLayoutMediaQuery);
	}

	document.addEventListener("CmsConsoleConnected", e => EventBus.cmsConsoleConnected = true);
	document.addEventListener("CmsConsoleDisconnected", e => EventBus.cmsConsoleConnected = false);
	EventBus.cmsConsoleConnected = EventBus.cmsEventStream.isConnected();

	EventBus.tamStatus = EventBus.cmsEventStream.get(CmsKey.CmsTAMStatus);
	document.addEventListener(CmsKey.CmsTAMStatus, e => { EventBus.tamStatus = e.detail; });

	EventBus.contentSyncStatus = EventBus.cmsEventStream.get(CmsKey.CmsContentSyncStatus);
	document.addEventListener(CmsKey.CmsContentSyncStatus, e => { EventBus.contentSyncStatus = e.detail; });

	EventBus.loeStatus = EventBus.cmsEventStream.get(CmsKey.CmsLoEStatus);
	document.addEventListener(CmsKey.CmsLoEStatus, e => { EventBus.loeStatus = e.detail; });
})();
function MeasureHeightOfId(id)
{
	let ele = document.getElementById(id);
	if (ele)
		return ele.offsetHeight;
	return 0;
}
window.appEventBus = EventBus; // Handle for debugging
export default EventBus;
